import React from 'react';
import {ChartComponentProps} from '@/views/storyboard/charts/ChartContainer';
import {getChartDataTotal} from '@/views/storyboard/utils/calculations/chartTotal';
import {AggregationsChartData, TimelineByDateChartData} from 'entities/chartData';
import {ChartType} from '@/views/storyboard/utils/chartConstants';
import {getDefaultDataLabelPosition} from '@/views/storyboard/utils/chartDataLabels';
import PieChart from './PieChart';
import ChartIndicators from '@/views/storyboard/charts/utils/ChartIndicators';


export type PieChartData = (
    TimelineByDateChartData
    | AggregationsChartData
    );

const PieChartWrapper = React.memo<ChartComponentProps>(({
    dataLooselyTyped,
    width,
    height,
    title,
    queryDuration = '0',
    chartSettingsView,
    dataLabelPosition = getDefaultDataLabelPosition(ChartType.PieChart),
    description = '',
    datePeriod = '',
    onPreviewFooterChange,
}) => {
    const data = dataLooselyTyped as PieChartData[];

    // If the original dictionary doesn't contain a date key
    // then use the string "key" as namekey for piechart
    let nameKey: string;
    if ('labelShort' in data[0]) {
        nameKey = 'labelShort';
    } else {
        nameKey = 'key';
    }

    const filtered = data.filter((row) => row.docCount !== 0);
    const filteredAndSorted = filtered.sort((a, b) => b.docCount - a.docCount);

    const comparableKeys: Array<keyof TimelineByDateChartData> = [
        'labelFullComparable',
        'labelNameComparable',
        'labelSearchComparable',
        'labelShortComparable',
    ];
    for (let i = 0; i < filtered.length; i++) {
        const comparableKeysExist = comparableKeys.every(key =>
            Object.keys(filtered[i]).includes(key)
        );
        if (comparableKeysExist) {
            // @ts-expect-error
            comparableKeys.forEach(key => delete filtered[i][key]);
        }
    }

    const total = getChartDataTotal(filtered, ['docCount']);

    const seriesData =
        filteredAndSorted.map((item) => ({
            name: item[nameKey as keyof typeof item],
            value: item.docCount,
            share: (item.relativeToAllDocs * 100).toFixed(2),
            total: item.allDocs,
        }));

    return (
        <>
            <PieChart
                data={seriesData}
                height={height}
                width={width}
                chartSettingsView={chartSettingsView}
                title={title}
                dataLabelPosition={dataLabelPosition}
                description={description}
                datePeriod={datePeriod}
            />
            <ChartIndicators
                total={total}
                totalComparable={0}
                trendValue="0"
                queryDuration={queryDuration}
                chartQueryOutput={undefined}
                chartSettingsView={chartSettingsView}
                onPreviewFooterChange={onPreviewFooterChange}
            />
        </>
    );
});

export default PieChartWrapper;
